import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { Box, useTheme } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { IconButton, Tooltip, Typography } from '@zendesk/forethought-elements';
import moreIcon from 'src/pages/workflow-builder-edit/assets/icons/more-icon.svg';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';

interface OrchestratorDropdownMenuProps {
  onDeleteClicked: () => void;
  onDuplicateClicked?: () => void;
  onEditClicked: () => void;
  workflowId: string;
  workflowName: string;
}

export const OrchestratorDropdownMenu = ({
  onDeleteClicked,
  onDuplicateClicked,
  onEditClicked,
  workflowId,
  workflowName,
}: OrchestratorDropdownMenuProps) => {
  const { t } = useTranslation('orchestrator');
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label={t('dropdownMenu.menuAriaLabel', { workflowName })}
        onClick={handleClick}
        variant='ghost'
      >
        <ReactSVG src={moreIcon} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        onClose={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleClose();
        }}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            border: `1px solid ${palette.colors.grey[200]}`,
            borderRadius: '4px',
            boxShadow:
              '0px 0px 1px rgba(0, 12, 32, 0.04), 2px 5px 8px rgba(3, 17, 38, 0.107135)',
            padding: '4px 0',
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <MenuItem
          aria-label={t('dropdownMenu.editAriaLabel')}
          disableRipple
          onClick={e => {
            e.stopPropagation();
            onEditClicked();
            handleClose();
          }}
          sx={{
            '&:hover': {
              backgroundColor: palette.colors.primary[100],
            },
          }}
        >
          <Typography variant='font14'>{t('dropdownMenu.edit')}</Typography>
        </MenuItem>
        {onDuplicateClicked && (
          <MenuItem
            aria-label={t('dropdownMenu.duplicateAriaLabel', { workflowName })}
            disableRipple
            onClick={e => {
              e.stopPropagation();
              onDuplicateClicked();
              handleClose();
            }}
            sx={{
              '&:hover': {
                backgroundColor: palette.colors.primary[100],
              },
            }}
          >
            <Typography variant='font14'>
              {t('dropdownMenu.duplicate')}
            </Typography>
          </MenuItem>
        )}
        <MenuItem
          aria-label={t('dropdownMenu.automationIdTooltip')}
          disableRipple
          onClick={e => {
            e.stopPropagation();
            navigator.clipboard.writeText(workflowId);
            dispatch(
              setGlobalToastOptions({
                title: t('dropdownMenu.toastIdCopied'),
                variant: 'main',
              }),
            );
            handleClose();
          }}
          sx={{
            '&:hover': {
              backgroundColor: palette.colors.primary[100],
            },
          }}
        >
          <Tooltip tooltipContent={t('dropdownMenu.automationIdTooltip')}>
            <Box
              sx={{
                display: 'flex',
                gap: '4px',
              }}
            >
              <Typography variant='font14'>
                {t('dropdownMenu.automationId')}
              </Typography>
            </Box>
          </Tooltip>
        </MenuItem>
        <MenuItem
          aria-label={t('dropdownMenu.deleteAriaLabel', { workflowName })}
          disableRipple
          onClick={e => {
            e.stopPropagation();
            onDeleteClicked();
            handleClose();
          }}
          sx={{
            '&:hover': {
              backgroundColor: palette.colors.primary[100],
            },
          }}
        >
          <Typography color={palette.colors.red[500]} variant='font14'>
            {t('conditionNode.deleteConfirm')}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};
